/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import axios from "@/api/axios";
import ChangePassword from "./ChangePassword";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

export default function ForgotPassword({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [userCredentials, setUserCredentials] = useState({
    orgId: "",
    username: "",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    reset,
  } = useForm({
    mode: "onTouched", // Only validate after field is touched
    reValidateMode: "onChange",
    defaultValues: {
      orgId: "",
      username: "",
    },
  });

  const getFieldValidation = (fieldName) => {
    const hasError = errors[fieldName];
    const isTouched = touchedFields[fieldName];

    // Only show error state if field has been touched and has error
    if (isTouched && hasError) return "error";
    // Show default state if not touched or while typing
    return "default";
  };

  const shouldShowError = (fieldName) => {
    return touchedFields[fieldName] && !!errors[fieldName];
  };

  // Reset form when dialog opens to ensure clean state
  useEffect(() => {
    if (open) {
      reset({
        orgId: "",
        username: "",
      });
    }
  }, [open, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    // Store user credentials and open change password dialog regardless of API result
    setUserCredentials({
      orgId: data.orgId.trim(),
      username: data.username.trim(),
    });

    reset(); // Clear form

    // Open change password dialog (don't call onClose to avoid parent state conflicts)
    setChangePasswordOpen(true);

    // Send API request in background
    try {
      await axios.post("/api/v1/auth/password-reset-request", {
        organization_acronym: data.orgId.trim(),
        username: data.username.trim(),
      });
    } catch (error) {
      // Silently handle error - user will know if reset code doesn't arrive
      console.error("Password reset request failed:", error);
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    reset(); // Clear form when closing
    onClose();
  };

  const handleChangePasswordClose = () => {
    setChangePasswordOpen(false);
    setUserCredentials({ orgId: "", username: "" });
    // Close the forgot password dialog when change password dialog closes
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.5rem",
            pb: 2,
          }}
        >
          {t("forgotPassword.title")}
        </DialogTitle>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <DialogContent sx={{ pb: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>
              {t("forgotPassword.description")}
            </DialogContentText>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <StyledTextField
                label={t("forgotPassword.orgIdLabel")}
                autoComplete="organization"
                type="text"
                autoFocus
                {...register("orgId", {
                  required: t("forgotPassword.orgIdRequired"),
                  pattern: {
                    value: /^[A-Za-z0-9_-]+$/,
                    message: t("forgotPassword.orgIdPattern"),
                  },
                  minLength: {
                    value: 2,
                    message: t("forgotPassword.orgIdMinLength"),
                  },
                })}
                error={shouldShowError("orgId")}
                helperText={
                  shouldShowError("orgId") ? errors?.orgId?.message : ""
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon
                          color={
                            getFieldValidation("orgId") === "error"
                              ? "error"
                              : "action"
                          }
                        />
                      </InputAdornment>
                    ),
                  },
                }}
                fullWidth
                variant="outlined"
              />

              <StyledTextField
                label={t("forgotPassword.usernameLabel")}
                autoComplete="username"
                type="text"
                {...register("username", {
                  required: t("forgotPassword.usernameRequired"),
                  minLength: {
                    value: 3,
                    message: t("forgotPassword.usernameMinLength"),
                  },
                  pattern: {
                    value: /^[A-Za-z0-9._@-]+$/,
                    message: t("forgotPassword.usernamePattern"),
                  },
                })}
                error={shouldShowError("username")}
                helperText={
                  shouldShowError("username") ? errors?.username?.message : ""
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon
                          color={
                            getFieldValidation("username") === "error"
                              ? "error"
                              : "action"
                          }
                        />
                      </InputAdornment>
                    ),
                  },
                }}
                fullWidth
                variant="outlined"
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ pb: 3, px: 3, pt: 1 }}>
            <Button onClick={handleClose} disabled={isLoading}>
              {t("forgotPassword.cancelButton")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isLoading || !isValid}
              sx={{
                minWidth: 120,
                py: 1,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {isLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  <span>{t("forgotPassword.sendingRequest")}</span>
                </Box>
              ) : (
                t("forgotPassword.submitButton")
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Change Password Dialog */}
      <ChangePassword
        open={changePasswordOpen}
        onClose={handleChangePasswordClose}
        onSuccess={(message) => {
          // Close both dialogs and call parent onSuccess
          setChangePasswordOpen(false);
          setUserCredentials({ orgId: "", username: "" });
          onClose(); // Close forgot password dialog
          if (onSuccess) onSuccess(message);
        }}
        initialOrgId={userCredentials.orgId}
        initialUsername={userCredentials.username}
      />
    </>
  );
}
